import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { classesApi, type ClassResponse } from '@/api/endpoints/classes'
import { useUI } from '@/stores/ui'
import { useAuth } from '@/stores/auth'
import { isAdminRole } from '@/utils/roles'
import TeacherPicker from '@/components/dashboard/TeacherPicker'

type EditableClass = Pick<ClassResponse, 'id' | 'name'> & Partial<ClassResponse>

interface Props {
  open: boolean
  onClose: () => void
  cls: EditableClass
  onSaved?: (updated: ClassResponse) => void
}

// API dates are ISO datetimes ("2026-09-02T00:00:00"); <input type="date"> needs "YYYY-MM-DD".
const toDateInput = (v?: string | null) => (v ? v.slice(0, 10) : '')

export default function EditClassModal({ open, onClose, cls, onSaved }: Props) {
  const addToast = useUI((state) => state.addToast)
  const { user } = useAuth()
  const isAdmin = isAdminRole(user?.role)
  const [name, setName] = useState(cls.name)
  const [notes, setNotes] = useState(cls.notes ?? '')
  const [address, setAddress] = useState(cls.address ?? cls.location ?? '')
  const [weeklyTime, setWeeklyTime] = useState(cls.weekly_time_description ?? cls.weekly_time_desc ?? '')
  const [startDate, setStartDate] = useState(toDateInput(cls.start_date))
  const [teacherIds, setTeacherIds] = useState<string[]>(cls.teacher_ids ?? [])
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    setName(cls.name)
    setNotes(cls.notes ?? '')
    setAddress(cls.address ?? cls.location ?? '')
    setWeeklyTime(cls.weekly_time_description ?? cls.weekly_time_desc ?? '')
    setStartDate(toDateInput(cls.start_date))
    setTeacherIds(cls.teacher_ids ?? [])
  }, [cls])

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 hover-raise pointer-events-auto max-h-[90vh] overflow-y-auto"
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 22 }}
            role="dialog"
            aria-label="Edit Class"
          >
            <h2 className="text-xl font-semibold mb-1">Edit Class</h2>
            {cls.class_id && <div className="text-xs text-gray-500 mb-3">Class ID {cls.class_id} (never changes)</div>}
            <form
              className="space-y-3"
              onSubmit={async (e) => {
                e.preventDefault()
                setSubmitting(true)
                try {
                  // Send every field: the API replaces them, so omitting one would blank it.
                  const updated = await classesApi.update(cls.id, {
                    name,
                    notes: notes || null,
                    address: address || null,
                    weekly_time_description: weeklyTime || null,
                    start_date: startDate || null,
                    ...(isAdmin ? { teacher_ids: teacherIds } : {}),
                  })
                  addToast({ message: 'Class updated successfully', type: 'success' })
                  onSaved?.(updated)
                  onClose()
                } catch (error: any) {
                  addToast({
                    message: error.response?.data?.detail || 'Failed to update class',
                    type: 'error',
                  })
                } finally {
                  setSubmitting(false)
                }
              }}
            >
              <div>
                <label className="block text-sm font-medium mb-1" htmlFor="ec-name">Name</label>
                <input id="ec-name" className="w-full input-field focus:outline-none focus:ring-2 focus:ring-yellow-400/60 transition-shadow" value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" htmlFor="ec-notes">Notes</label>
                <textarea
                  id="ec-notes"
                  className="w-full input-field focus:outline-none focus:ring-2 focus:ring-yellow-400/60 transition-shadow min-h-[100px]"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Misc notes, reminders, etc."
                  maxLength={1023}
                />
                <div className="text-xs text-gray-500 mt-1">{notes.length}/1023</div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" htmlFor="ec-address">Location</label>
                <input id="ec-address" className="w-full input-field focus:outline-none focus:ring-2 focus:ring-yellow-400/60 transition-shadow" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Parish Hall - Room 101" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" htmlFor="ec-weekly">Weekly Time Description</label>
                <input id="ec-weekly" className="w-full input-field focus:outline-none focus:ring-2 focus:ring-yellow-400/60 transition-shadow" value={weeklyTime} onChange={(e) => setWeeklyTime(e.target.value)} placeholder="Every Monday at 6:30 PM" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" htmlFor="ec-start">Start Date</label>
                <input id="ec-start" type="date" className="w-full input focus:outline-none focus:ring-2 focus:ring-yellow-400/60 transition-shadow" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </div>
              {isAdmin && (
                <div>
                  <label className="block text-sm font-medium mb-1">Teachers</label>
                  <TeacherPicker value={teacherIds} onChange={setTeacherIds} />
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" className="btn-secondary transition-transform duration-200 hover:scale-[1.02] active:scale-[0.98]" onClick={onClose} disabled={submitting}>Cancel</button>
                <button type="submit" className="btn-primary transition-transform duration-200 hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-yellow-400/60" disabled={submitting} title="Save all changes to this class, including name, notes, location, weekly time and teachers">{submitting ? 'Saving…' : 'Save'}</button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
